import lodash from 'lodash';
import { FINAL_ORDER_STATES, V3_ORDER_FINISHED_STATUSES, ORDER_STATUS_UPDATE_STRATEGY, } from '../constants.js';
const { chunk } = lodash;
const isResolvingOrder = ({ exodusStatus }) => !FINAL_ORDER_STATES.has(exodusStatus ?? '');
const isResolvingOrdersWithAvailableAssets = ({ order, availableAssets, }) => {
    const { fromAsset, toAsset } = order;
    if (!fromAsset || !toAsset)
        return false;
    return !!availableAssets[fromAsset] && !!availableAssets[toAsset] && isResolvingOrder(order);
};
export const shouldCheckOrder = ({ order, availableAssets, }) => {
    const { exodusStatus } = order;
    const inProgress = exodusStatus === 'in-progress';
    const isSyncing = exodusStatus === 'syncing';
    const resolving = isResolvingOrdersWithAvailableAssets({ order, availableAssets });
    return resolving && (inProgress || isSyncing);
};
export const batchProcessOrders = async (orders, callback) => {
    const batchSize = 40;
    const results = await Promise.all(chunk(orders, batchSize).map(callback));
    return results.flat();
};
export const getOrderUpdates = ({ v3Order, toTx, isIndexlessOrder, txTypes, order, orderStatusUpdateStrategy, isRestoringAssets, fromAsset, toAsset, }) => {
    const toTxId = v3Order.toTransactionId || (toTx?.txId ?? '');
    const updates = {
        toTxId,
        status: v3Order.status,
    };
    const nextTxIds = order.txIds ? [...order.txIds] : [];
    const hasDeposit = nextTxIds.some((tx) => tx.txType === txTypes.DEPOSIT);
    const hasPayout = nextTxIds.some((tx) => tx.txType === txTypes.PAYOUT);
    if (!order.fromTxId && v3Order.fromTransactionId) {
        updates.fromTxId = v3Order.fromTransactionId;
        if (!hasDeposit) {
            nextTxIds.push({ txType: txTypes.DEPOSIT, txId: v3Order.fromTransactionId });
        }
    }
    const needsToSetPayoutTx = nextTxIds.some((tx) => tx.txType === txTypes.DEPOSIT) && !hasPayout && Boolean(toTxId);
    if (needsToSetPayoutTx) {
        nextTxIds.push({ txType: txTypes.PAYOUT, txId: toTxId });
    }
    if (nextTxIds.length !== (order.txIds?.length ?? 0)) {
        updates.txIds = nextTxIds;
    }
    if (v3Order.amount) {
        const fromAmount = fromAsset.currency.defaultUnit(v3Order.amount.value);
        if (!order.fromAmount || !fromAmount.equals(order.fromAmount)) {
            updates.fromAmount = fromAmount;
        }
    }
    if (v3Order.toAmount) {
        const toAmount = toAsset.currency.defaultUnit(v3Order.toAmount.value);
        if (!order.toAmount || !toAmount.equals(order.toAmount)) {
            updates.toAmount = toAmount;
        }
    }
    const finishedServerSide = V3_ORDER_FINISHED_STATUSES.has(v3Order.status);
    const completedServerSide = finishedServerSide && v3Order.status === 'complete';
    const isSameChainSelfTx = Boolean(toTxId) && toTxId === v3Order.fromTransactionId;
    const finishedClientSide = Boolean(toTx) && !toTx?.pending && !isSameChainSelfTx;
    const completedClientSide = finishedClientSide && !toTx?.failed;
    if (completedServerSide && isRestoringAssets) {
        updates.status = 'complete-verified';
        return updates;
    }
    if (orderStatusUpdateStrategy === ORDER_STATUS_UPDATE_STRATEGY.CLIENT_OR_SERVER) {
        if (finishedServerSide) {
            updates.status = completedServerSide ? 'complete-verified' : 'failed-final';
            return updates;
        }
        if (finishedClientSide) {
            updates.status = completedClientSide ? 'complete-verified' : 'failed-final';
            return updates;
        }
        return updates;
    }
    if (orderStatusUpdateStrategy === ORDER_STATUS_UPDATE_STRATEGY.CLIENT_AND_SERVER &&
        finishedServerSide) {
        if (!completedServerSide) {
            updates.status = 'failed-final';
            return updates;
        }
        if (isIndexlessOrder || isSameChainSelfTx) {
            updates.status = 'complete-verified';
            return updates;
        }
        if (finishedClientSide) {
            updates.status = completedClientSide ? 'complete-verified' : 'failed-final';
        }
        return updates;
    }
    return updates;
};
